import {
  ChatCloseIcon,
  ChatMicIcon,
  ChatSendIcon,
  ChatStopRecordingIcon,
} from '../../../assets/svg';
import { lightTheme } from '../../../styles/theme';
import { ComposerIconButton, SendButton } from '../chatSurfaces.styled';
import { formatSeconds } from '../helpers/chatFormat';
import { useBarCount } from '../hooks/useBarCount';
import {
  Bar,
  Hint,
  RecordButton,
  RecorderBar,
  Timer,
  Waveform,
} from './VoiceRecorderBar.styled';

/** One bar plus the space beside it, at the density the design draws them. */
const BAR_PITCH = 7;

/**
 * Replaces the composer while recording. Two states, matching the design:
 * idle (prompt to start) and recording (stop, live waveform, timer).
 */
export const VoiceRecorderBar = ({
  recording,
  seconds,
  onStart,
  onStop,
  onCancel,
}: {
  recording: boolean;
  seconds: number;
  onStart: () => void;
  onStop: () => void;
  onCancel: () => void;
}) => {
  // The waveform only exists while recording, so the ref only exists then.
  const wave = useBarCount(BAR_PITCH, recording);

  return (
    <RecorderBar>
      {/* One button, two jobs: the disc never changes, only the glyph in it.
          Both are Font Awesome on the same grid, so a single 12px pin sizes
          the mic and the (visually heavier) solid square correctly. */}
      <RecordButton
        type="button"
        aria-label={recording ? 'Stop recording' : 'Start recording'}
        onClick={recording ? onStop : onStart}
      >
        {recording ? (
          <ChatStopRecordingIcon />
        ) : (
          <ChatMicIcon color={lightTheme.white} />
        )}
      </RecordButton>

      {recording ? (
        <Waveform ref={wave.ref} aria-hidden>
          {Array.from({ length: wave.count }).map((_, index) => (
            <Bar key={index} $index={index} />
          ))}
        </Waveform>
      ) : (
        <Hint>Click on microphone to start recording</Hint>
      )}

      {recording && <Timer>{formatSeconds(seconds)}</Timer>}

      <ComposerIconButton
        type="button"
        aria-label="Discard recording"
        onClick={onCancel}
      >
        <ChatCloseIcon />
      </ComposerIconButton>

      <SendButton
        type="button"
        aria-label="Finish recording"
        onClick={onStop}
        disabled={!recording}
      >
        {/* Always white - the disc under it is what changes colour. */}
        <ChatSendIcon color={lightTheme.white} />
      </SendButton>
    </RecorderBar>
  );
};
